// Excel Processing with Image Optimization and JSZip Import
import JSZip from 'jszip';

window.excelProcessing = {
    /**
     * Process an XLSX file with image optimization
     * @param {string} base64Data - The Excel file as a base64 string
     * @returns {Promise<string>} - Processed Excel file as base64 string
    */
    
    processXlsxWithImageOptimization: async function (base64Data) {
        try {
            // Convert base64 to array buffer
            const arrayBuffer = this._base64ToArrayBuffer(base64Data);

            // Create a new JSZip instance
            const zip = new JSZip();

            // Load the Excel file (which is a ZIP archive)
            const loadedZip = await zip.loadAsync(arrayBuffer);

            // Process images in the Excel file
            await this._processImagesInZip(loadedZip);

            // Generate the new ZIP file
            const processedZipBlob = await loadedZip.generateAsync({
                type: 'blob',
                compression: 'DEFLATE',
                compressionOptions: {
                    level: 9 // Maximum compression
                }
            });

            // Convert the blob back to base64
            return await this._blobToBase64(processedZipBlob);
        } catch (error) {
            console.error('Error processing Excel file:', error);
            // Return the original data if processing fails
            return base64Data;
        }
    },

    /**
     * Process images within the ZIP file
     * @param {JSZip} zipFile - The loaded JSZip object
     */
    _processImagesInZip: async function (zipFile) {
        // Look for the media folder in the Excel file structure
        const mediaFolderPattern = /xl\/media\/.+\.(png|jpe?g|gif)/i;

        // Get all files that match the pattern
        const imageFiles = Object.keys(zipFile.files).filter(filename =>
            mediaFolderPattern.test(filename));

        // Process each image file
        for (const imagePath of imageFiles) {
            try {
                // Get the image data
                const imageBlob = await zipFile.file(imagePath).async('blob');

                // Optimize the image
                const optimizedImageBlob = await this._optimizeImage(imageBlob, imagePath);

                // Replace the original image with the optimized one if it's smaller
                if (optimizedImageBlob.size < imageBlob.size) {
                    zipFile.file(imagePath, optimizedImageBlob);
                }
            } catch (error) {
                console.warn(`Failed to optimize image ${imagePath}:`, error);
                // Continue with other images if one fails
            }
        }
    },

    /**
     * Optimize an image using canvas and WebP conversion
     * @param {Blob} imageBlob - The original image as Blob
     * @param {string} imagePath - The path of the image in the ZIP
     * @returns {Promise<Blob>} - Optimized image as Blob
     */
    _optimizeImage: async function (imageBlob, imagePath) {
        const self = this; // Store reference to 'this' for use inside Promise
        return new Promise((resolve, reject) => {
            const img = new Image();
            const url = URL.createObjectURL(imageBlob);

            img.onload = () => {
                URL.revokeObjectURL(url);

                // Create a canvas element
                const canvas = document.createElement('canvas');
                const ctx = canvas.getContext('2d');

                // Set canvas dimensions to match the image
                canvas.width = img.width;
                canvas.height = img.height;

                // Draw the image on the canvas
                ctx.drawImage(img, 0, 0);

                // Check if browser supports WebP
                const supportsWebP = self._isWebPSupported();

                if (supportsWebP) {
                    // Use WebP with appropriate quality
                    // For images that need transparency (like PNG), use higher quality
                    const isPNG = imagePath.toLowerCase().endsWith('.png');
                    const webpQuality = isPNG ? 0.9 : 0.85;

                    // Convert to WebP
                    canvas.toBlob(
                        blob => {
                            // If WebP conversion results in smaller size, use it
                            if (blob.size < imageBlob.size) {
                                resolve(blob);
                            } else {
                                // Otherwise use regular optimization
                                self._fallbackOptimize(canvas, imageBlob, imagePath, resolve);
                            }
                        },
                        'image/webp',
                        webpQuality
                    );
                } else {
                    // Fallback to regular optimization if WebP is not supported
                    self._fallbackOptimize(canvas, imageBlob, imagePath, resolve);
                }
            };

            img.onerror = () => {
                URL.revokeObjectURL(url);
                reject(new Error('Failed to load image'));
            };

            img.src = url;
        });
    },

    /**
     * Fallback optimization using original format
     * @param {HTMLCanvasElement} canvas - The canvas with the image
     * @param {Blob} originalBlob - The original image blob
     * @param {string} imagePath - The image path
     * @param {Function} resolve - The promise resolve function
     */
    _fallbackOptimize: function (canvas, originalBlob, imagePath, resolve) {
        // Determine the image type and quality
        let mimeType = 'image/jpeg';
        let quality = 0.85; // 85% quality for JPEG

        if (imagePath.toLowerCase().endsWith('.png')) {
            mimeType = 'image/png';
            quality = 0.9; // 90% quality for PNG
        } else if (imagePath.toLowerCase().endsWith('.gif')) {
            mimeType = 'image/gif';
            // No quality setting for GIF, will use default
        }

        // Convert canvas to blob with compression
        canvas.toBlob(
            blob => resolve(blob),
            mimeType,
            quality
        );
    },

    /**
     * Check if the browser supports WebP format
     * @returns {boolean} - Whether WebP is supported
     */
    _isWebPSupported: function () {
        // We assume modern browsers support WebP
        // A more thorough check would involve creating a canvas and checking toDataURL
        return true;
    },

    /**
     * Convert base64 to ArrayBuffer
     * @param {string} base64 - Base64 string
     * @returns {ArrayBuffer} - Array buffer
     */
    _base64ToArrayBuffer: function (base64) {
        const binaryString = window.atob(base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    },

    /**
     * Convert Blob to base64
     * @param {Blob} blob - The blob to convert
     * @returns {Promise<string>} - Base64 string
     */
    _blobToBase64: function (blob) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                // Remove the "data:application/octet-stream;base64," part
                const base64 = reader.result.split(',')[1];
                resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });
    }
};